import {AxiosResponse} from "axios";
import axios from 'axios';
import { getTokenExpiration, isTokenExpired } from '@/utils/jwt';
import {notification} from "ant-design-vue";
import { i18n } from '@/services/i18n';

interface AuthResponse {
    access_token: string;
    refresh_token: string;
    id_token: string;
}

class KeycloakAuthService {
    private refreshTimeout: number | null = null;

    constructor() {
        this.loadTokens();
    }

    public getTokensByCodeAndState(code: string, state: string): Promise<void | AxiosResponse<any>> {
        return axios.get<AuthResponse>(process.env.VUE_APP_BACKEND_URL ? process.env.VUE_APP_BACKEND_URL + '/auth/exchange-tokens' : '/auth/exchange-tokens', {
            params: {
                code,
                state,
                redirect_uri: encodeURIComponent(`${window.location.origin}/authorization`)
            }
        }).then(response => {
            return this.saveTokens(response.data);
        }).catch(() => {
            this.clearData();
        });
    }

    public async loadAccessToken() {
        const token = this.getAccessToken();

        if (token) {
            if (isTokenExpired(token)) {
                await this.refreshToken();

                return this.getAccessToken();
            } else {
                return Promise.resolve(token);
            }
        }

        return null;
    }

    private async saveTokens(data: AuthResponse): Promise<void> {
        const expiresAt = getTokenExpiration(data.access_token);

        return axios.get(process.env.VUE_APP_BACKEND_URL ? `${process.env.VUE_APP_BACKEND_URL}/api/users/me` : `/api/users/me`, {
            headers: {
                Authorization: `Bearer ${data.access_token}`
            }
        }).then((response) => {
            localStorage.setItem('AUTH', JSON.stringify({
                access_token: data.access_token,
                refresh_token: data.refresh_token,
                expires_at: expiresAt.toString(),
                user_id: response.data.id,
            }));

            this.scheduleTokenRefresh();
        }).catch(error => {
            console.error(error);

            notification.error({
                message: i18n.t('auth.errors.errorGetUserData') as string,
                description: '',
                duration: 5,
            });

            this.logout().finally(() => {
                setTimeout(() => {
                    window.location.replace('/login');
                }, 3000);
            })
        });
    }

    private scheduleTokenRefresh(): void {
        if (this.refreshTimeout) {
            clearTimeout(this.refreshTimeout);
        }

        const timeLeft = this.getExpiresDate() - Date.now();

        const refreshTime = Math.max(timeLeft - 60000, 0);

        if (!refreshTime) {
            return;
        }

        this.refreshTimeout = setTimeout(() => {
            this.refreshToken().catch(() => {
                this.logout().finally(() => {
                    notification.error({
                        message: i18n.t('auth.errors.errorCheckAccess') as string,
                        description: '',
                        duration: 7,
                    });

                    setTimeout(() => {
                        window.location.replace('/login');
                    }, 5000);
                });
            });
        }, refreshTime);
    }

    public async refreshToken(): Promise<void> {
        const refreshToken = this.getRefreshToken();

        if (!refreshToken) {
            return Promise.reject();
        }

        return axios.post<AuthResponse>(
            process.env.VUE_APP_BACKEND_URL ? process.env.VUE_APP_BACKEND_URL + '/auth/refresh' : '/auth/refresh',
            { refresh_token: refreshToken }
        )
            .then((response) => {
                return this.saveTokens(response.data);
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    }

    public async logout(): Promise<any> {
        const refreshToken = this.getRefreshToken();

        if (!refreshToken) {
            this.clearData();

            return Promise.resolve();
        }

        return axios.post(process.env.VUE_APP_BACKEND_URL ? process.env.VUE_APP_BACKEND_URL + '/auth/logout' : '/auth/logout', {
            refresh_token: refreshToken,
        })
            .catch((error) => {
                Promise.reject(error);
            })
            .finally(() => {
                this.clearData();
            });
    }

    private loadTokens(): void {
        const accessToken = this.getAccessToken();
        const refreshToken = this.getRefreshToken();
        const expiresAt = this.getExpiresDate();

        if (accessToken && refreshToken && expiresAt) {
            const timeLeft = expiresAt - Date.now();

            if (timeLeft > 0) {
                this.scheduleTokenRefresh();
            } else {
                this.clearData();
            }
        }
    }

    public clearData(): void {
        localStorage.removeItem('AUTH');
        localStorage.removeItem('userData');
        localStorage.removeItem('sp_notifications');

        if (this.refreshTimeout) {
            clearTimeout(this.refreshTimeout);

            this.refreshTimeout = null;
        }
    }

    public getAccessToken(): string | null {
        const authData = localStorage.getItem('AUTH');

        return authData ? JSON.parse(authData).access_token : null;
    }

    public getRefreshToken(): string | null {
        const authData = localStorage.getItem('AUTH');

        return authData ? JSON.parse(authData).refresh_token : null;
    }

    public getExpiresDate(): number {
        const authData = localStorage.getItem('AUTH');

        return authData ? Number(JSON.parse(authData).expires_at) : 0;
    }

    public isAuthenticated(): boolean {
        const token = this.getAccessToken();

        return !!token && !isTokenExpired(token);
    }
}

export const keycloakAuthService = new KeycloakAuthService();
